import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '@feathr/blackbox';
import { Alert } from '@feathr/components';

import type { IReachValidationResult } from '../AdWizardAudienceStep';
import { getAudienceThresholdBand, MIN_REACHABLE_AUDIENCE } from '../AdWizardAudienceStep';
import { shouldShowNarrowingFilterCallout } from './AdWizardFiltersStep.utils';

import * as styles from './AdWizardFiltersStep.css';

interface IProps {
  campaign: Campaign;
  reachValidation: IReachValidationResult;
  hasAudienceTargetings: boolean;
}

/** Warns against adding narrowing filters when the reachable audience is below or near the minimum. */
function AdWizardFiltersStepNarrowingFilterCallout({
  campaign,
  reachValidation,
  hasAudienceTargetings,
}: Readonly<IProps>): JSX.Element | null {
  const { t } = useTranslation();
  // Band on display reach (which holds the total audience before a channel is chosen); only a
  // Meta-only campaign is exempt, since location/age/interest filters can't narrow Meta.
  const isMetaOnly = reachValidation.appliesToMeta && !reachValidation.appliesToDisplay;
  const band = isMetaOnly ? 'clear' : getAudienceThresholdBand(reachValidation.displayReach);

  if (
    !shouldShowNarrowingFilterCallout({
      campaign,
      band,
      isLoading: reachValidation.isLoading,
      hasAudienceTargetings,
    })
  ) {
    return null;
  }

  const minimum = numeral(MIN_REACHABLE_AUDIENCE).format('0,0');
  const isBelow = band === 'below';

  return (
    <Alert
      className={styles.alert}
      description={
        isBelow
          ? t(
              'Adding location, age, or interest filters will narrow your audience further. Consider skipping filters for this campaign or broadening your audience first.',
            )
          : t(
              'Adding location, age, or interest filters could push your audience below the required minimum. If you apply filters, check your audience size before publishing.',
            )
      }
      name={'narrowing_filter_callout'}
      title={
        isBelow
          ? t('Your audience is below the {{minimum}} person minimum', { minimum })
          : t('Your audience is close to the {{minimum}} person minimum', { minimum })
      }
      type={'warning'}
    />
  );
}

export default observer(AdWizardFiltersStepNarrowingFilterCallout);
